import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, AlertTriangle, Loader2, Save, CreditCard, Globe, User as UserIcon,
} from 'lucide-react';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';

type PlanId = 'starter' | 'growth' | 'pro';

interface WorkspaceDetail {
  workspace: {
    id: string;
    name: string;
    slug: string;
    plan_id: PlanId;
    trial_ends_at: string | null;
    created_at: string;
    owner_id: string;
    owner_email: string | null;
  };
  sites: { site_id: string; role: string; created_at: string }[];
  subscription: {
    plan_id: PlanId;
    status: string;
    stripe_subscription_id: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
  } | null;
  effective_plan_id: PlanId;
  override_masked_by_subscription: boolean;
}

interface SiteGrantStatus {
  totalActiveProviders: number;
  grantedCount: number;
  fullyGranted: boolean;
}

const PLAN_OPTIONS: { value: PlanId; label: string }[] = [
  { value: 'starter', label: 'Starter' },
  { value: 'growth', label: 'Growth' },
  { value: 'pro', label: 'Pro' },
];

export default function PlatformWorkspaceDetail() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState<WorkspaceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Tier override draft state.
  const [draft, setDraft] = useState<PlanId | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Per-site "grant all platform AI providers" state.
  const [aiGrants, setAiGrants] = useState<Record<string, SiteGrantStatus>>({});
  const [aiToggling, setAiToggling] = useState<Record<string, boolean>>({});

  const load = () => {
    if (!id) return;
    setLoading(true);
    api.get<WorkspaceDetail>(`/admin/platform/settings/workspaces/${id}`)
      .then((res) => {
        setData(res);
        setDraft(null);
      })
      .catch((err) => setError(err?.message || 'Failed to load workspace'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  // Load AI-grant status for each linked site once the workspace is loaded.
  useEffect(() => {
    if (!data?.sites?.length) return;
    let cancelled = false;
    Promise.all(
      data.sites.map((s) =>
        api
          .get<SiteGrantStatus>(`/admin/platform/ai/grants/site/${encodeURIComponent(s.site_id)}`)
          .then((res) => [s.site_id, res] as const)
          .catch(() => null),
      ),
    ).then((results) => {
      if (cancelled) return;
      const next: Record<string, SiteGrantStatus> = {};
      for (const r of results) if (r) next[r[0]] = r[1];
      setAiGrants(next);
    });
    return () => { cancelled = true; };
  }, [data?.sites]);

  async function toggleAiGrant(siteId: string, granted: boolean) {
    setAiToggling((m) => ({ ...m, [siteId]: true }));
    try {
      const res = await api.put<SiteGrantStatus>(
        `/admin/platform/ai/grants/site/${encodeURIComponent(siteId)}`,
        { granted },
      );
      setAiGrants((m) => ({ ...m, [siteId]: res }));
    } catch {
      /* leave checkbox state unchanged on error */
    } finally {
      setAiToggling((m) => ({ ...m, [siteId]: false }));
    }
  }

  async function handleSave() {
    if (!id || !draft) return;
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await api.patch<{
        workspace: { id: string; plan_id: PlanId };
        effective_plan_id: PlanId;
        override_masked_by_subscription: boolean;
      }>(`/admin/platform/settings/workspaces/${id}`, { plan_id: draft });
      setData((prev) => prev && {
        ...prev,
        workspace: { ...prev.workspace, plan_id: res.workspace.plan_id },
        effective_plan_id: res.effective_plan_id,
        override_masked_by_subscription: res.override_masked_by_subscription,
      });
      setDraft(null);
      setSaveMsg(
        res.override_masked_by_subscription
          ? `Saved — but an active Stripe subscription (${res.effective_plan_id}) still overrides this.`
          : `Saved — tier is now ${res.effective_plan_id}.`
      );
    } catch (err: any) {
      setSaveMsg(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  if (!user?.isPlatformAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-900 font-medium">Platform admin access required.</p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-8 flex items-center gap-3 text-stone-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading workspace…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <Link to="/admin/platform/workspaces" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to workspaces
        </Link>
        <Card className="p-6 border-red-200 bg-red-50">
          <p className="text-red-900">{error || 'Workspace not found'}</p>
        </Card>
      </div>
    );
  }

  const { workspace, sites, subscription, effective_plan_id, override_masked_by_subscription } = data;
  const dirty = draft !== null && draft !== workspace.plan_id;

  return (
    <div className="max-w-4xl mx-auto p-8">
      <Link to="/admin/platform/workspaces" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to workspaces
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-2xl font-bold text-stone-900">{workspace.name}</h1>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-stone-100 text-stone-700 capitalize">
          {effective_plan_id}
        </span>
      </div>
      <p className="text-stone-500 mb-6 font-mono text-sm">{workspace.slug}</p>

      <Card className="p-5 mb-6">
        <div className="flex items-center gap-2 mb-3 text-stone-600">
          <UserIcon className="w-4 h-4" />
          <span className="text-sm font-medium">Owner</span>
        </div>
        <p className="text-stone-900">{workspace.owner_email || '—'}</p>
        <p className="text-xs text-stone-500 font-mono">{workspace.owner_id}</p>
      </Card>

      <Card className="p-5 mb-6">
        <div className="flex items-center gap-2 mb-3 text-stone-600">
          <Globe className="w-4 h-4" />
          <span className="text-sm font-medium">Linked sites</span>
        </div>
        {sites.length === 0 ? (
          <p className="text-sm text-stone-500">No sites linked to this workspace.</p>
        ) : (
          <ul className="space-y-2">
            {sites.map((s) => {
              const g = aiGrants[s.site_id];
              const busy = !!aiToggling[s.site_id];
              return (
                <li key={s.site_id} className="flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <span className="font-mono text-stone-900">{s.site_id}</span>
                    <span className="ml-2 text-xs text-stone-500 capitalize">{s.role}</span>
                  </div>
                  <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none" title="Grant this site access to every platform AI provider (shared free-credit keys)">
                    {busy ? (
                      <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
                    ) : (
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-stone-300"
                        checked={!!g?.fullyGranted}
                        disabled={!g}
                        onChange={(e) => toggleAiGrant(s.site_id, e.target.checked)}
                      />
                    )}
                    <span className="text-xs text-stone-600">
                      All platform AI
                      {g ? <span className="text-stone-400"> ({g.grantedCount}/{g.totalActiveProviders})</span> : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-5 h-5 text-sage-700" />
          <div>
            <h2 className="text-lg font-semibold text-stone-900">Subscription Tier Override</h2>
            <p className="text-sm text-stone-500">
              Flip the workspace's <span className="font-mono">plan_id</span> without Stripe. An
              active Stripe subscription will still override this — use it for testing gated
              features across tiers, or for workspaces without a live subscription.
            </p>
          </div>
        </div>

        {subscription && (
          <div className="mb-4 p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-900">
            Active Stripe subscription: <span className="font-medium capitalize">{subscription.plan_id}</span>{' '}
            ({subscription.status}). The override below changes <span className="font-mono">workspaces.plan_id</span>,
            but resolved tier remains <span className="font-medium capitalize">{effective_plan_id}</span> while
            the subscription is live.
          </div>
        )}

        <div className="flex items-center gap-3">
          <select
            value={draft ?? workspace.plan_id}
            onChange={(e) => setDraft(e.target.value as PlanId)}
            disabled={saving}
            className="border border-stone-300 rounded px-3 py-2 text-sm bg-white"
          >
            {PLAN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
          </Button>
          {saveMsg && (
            <span
              className={`text-xs ${
                saveMsg.toLowerCase().includes('fail')
                  ? 'text-red-600'
                  : override_masked_by_subscription
                  ? 'text-amber-700'
                  : 'text-green-600'
              }`}
            >
              {saveMsg}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}
